import type { TaskSection } from "@taskflow/contracts";
import { ChevronDown } from "lucide-react";
import { type ReactNode, useId } from "react";

import { cn } from "@/shared/ui";

import { useTaskSectionCollapse } from "./taskSections";

/** Единая рамка секции панели: одинаковые отступы и заголовки во всех семи. */
export function Section({
  title,
  count,
  action,
  section,
  children,
  forceOpen = false,
}: {
  forceOpen?: boolean;
  title: string;
  // undefined в типе явно: счётчик и действие часто приходят из
  // необязательных полей, а exactOptionalPropertyTypes это различает.
  count?: number | undefined;
  action?: ReactNode | undefined;
  /** Задан — блок сворачивается, и его состояние хранится на панели. */
  section?: TaskSection | undefined;
  children: ReactNode;
}) {
  const collapse = useTaskSectionCollapse();
  const bodyId = useId();
  const collapsible = section !== undefined && collapse !== null;
  const open = forceOpen || !collapsible || collapse.isOpen(section);

  const heading = (
    <>
      {title}
      {count !== undefined && count > 0 && <span className="ml-1.5 tabular-nums">{count}</span>}
    </>
  );

  return (
    <section className="px-5 py-4">
      <div className={cn("flex items-center justify-between gap-3", open && "mb-2.5")}>
        <h3 className="min-w-0 text-[11px] font-bold tracking-wide text-faint uppercase">
          {collapsible ? (
            <button
              type="button"
              // Подпись у кнопки своя — текст заголовка. Всплывающая
              // подсказка называет действие, а не блок: заголовок уже
              // виден, и повторять его в ней незачем.
              title={open ? "Свернуть блок" : "Развернуть блок"}
              aria-expanded={open}
              aria-controls={bodyId}
              className="flex min-w-0 items-center gap-1 transition-colors hover:text-fg"
              onClick={() => collapse.toggle(section)}
            >
              <ChevronDown
                className={cn("size-3.5 shrink-0 transition-transform", !open && "-rotate-90")}
                aria-hidden
              />
              <span className="truncate">{heading}</span>
            </button>
          ) : (
            heading
          )}
        </h3>
        {/* Действие свёрнутого блока скрыто вместе с ним: «Добавить связь»
            над закрытым списком добавляла бы туда, чего не видно. */}
        {open && action}
      </div>
      {/* Содержимое остаётся в разметке и просто скрывается: свёрнутый
          блок не должен ронять уже загруженные комментарии и историю
          и запрашивать их заново при каждом развороте. */}
      <div id={bodyId} hidden={!open}>
        {children}
      </div>
    </section>
  );
}
